/**
 * Dados mocados da tela de E-mail (regioes showMail / listMail do prototipo
 * SGW v2).
 *
 * A integracao e por conta Google de CADA membro e comeca DESCONECTADA
 * (`_shell.html:1576` — `gmailOn: false`): o primeiro estado da tela e o
 * convite para conectar, nao a caixa de entrada.
 *
 * Nao existe pasta, arquivamento nem marcador no design — so a lista de
 * conversas, o e-mail aberto e as acoes Responder / Encaminhar / Abrir
 * contexto / Converter em demanda.
 */
import { findAdminSection } from './admin.ts'

/** Contexto ja existente na plataforma que o e-mail referencia. */
export type MockMailContext = {
  /** Codigo mostrado no botao ("Abrir DMD-0047"). */
  code: string
  /** O que o codigo e — usado no title do botao. */
  kind: string
  /**
   * Rota aberta pelo botao. Aponta para o modulo, nao para o registro: os
   * codigos deste mock nao existem na base real, entao um deep link resolveria
   * em 404. Com a integracao de verdade o vinculo carrega o id.
   */
  route: string
}

export type MockMailMessage = {
  id: string
  senderName: string
  senderAddress: string
  /** Iniciais do remetente para o avatar. */
  initials: string
  subject: string
  /** Primeira linha exibida na lista. */
  snippet: string
  bodyParagraphs: string[]
  /** ISO 8601 do recebimento. */
  receivedAt: string
  unread: boolean
  context: MockMailContext | null
}

/** Conta conectada — exibida na faixa da integracao depois do Conectar. */
export const MOCK_MAIL_ACCOUNT = 'ana.martins@wynk.com.br'

/**
 * Empresa correlacionado pelo DOMINIO do remetente contra os empresas
 * cadastrados na administracao.
 *
 * Protótipo `_shell.html:3574`:
 * `clientOf = addr => this.companies.find(c => c.domains.includes(dominio))`.
 * O nome do cliente NAO fica gravado na mensagem — ele e derivado, senao
 * cadastrar um dominio novo nao reclassificaria os e-mails ja recebidos.
 */
export function clientOfAddress(address: string): string | null {
  const dominio = (address.split('@')[1] ?? '').trim().toLowerCase()
  if (dominio === '') return null
  return indicePorDominio().get(dominio) ?? null
}

/** Indice dominio → nome do cliente, montado uma vez (a lista roda por linha). */
let indice: Map<string, string> | null = null
function indicePorDominio() {
  if (indice !== null) return indice
  indice = new Map()
  const secao = findAdminSection('companies')
  if (secao !== null && secao.kind === 'companies') {
    for (const row of secao.rows) {
      for (const dominio of row.domains) indice.set(dominio.replace(/^@/, '').toLowerCase(), row.name)
    }
  }
  return indice
}

export const MOCK_MAIL_MESSAGES: MockMailMessage[] = [
  {
    id: 'mail-1',
    senderName: 'Patrícia Loureiro',
    // dominio de "Banco Meridiano S.A." no cadastro de empresas
    senderAddress: 'patricia.loureiro@meridiano.com.br',
    initials: 'PL',
    subject: 'Ajuste no relatório de faturamento por área',
    snippet: 'O relatório está somando as áreas inativas junto com as ativas…',
    bodyParagraphs: [
      'Olá, Ana, tudo bem?',
      'O relatório de faturamento que abrimos ontem está somando as áreas inativas junto com as ativas, e o total do mês fica cerca de 12% acima do que temos no nosso fechamento. Consegui reproduzir em dois meses diferentes, então não parece caso isolado.',
      'Precisamos disso corrigido antes do fechamento de agosto, que fecha no dia 29. Se ajudar, posso mandar a planilha comparando os dois números.',
      'Obrigada!',
    ],
    receivedAt: '2026-08-07T15:42:00-03:00',
    unread: true,
    context: { code: 'DEM-2026-0412', kind: 'Demanda', route: '/demands/inbox' },
  },
  {
    id: 'mail-2',
    senderName: 'Marcelo Aguiar',
    // dominio de "Transportes Andrade"
    senderAddress: 'marcelo.aguiar@transportesandrade.com.br',
    initials: 'MA',
    subject: 'Prazo do módulo de aprovações',
    snippet: 'Conseguimos manter a entrega do módulo de aprovações para o dia 21?',
    bodyParagraphs: [
      'Bom dia, Ana.',
      'Conseguimos manter a entrega do módulo de aprovações para o dia 21 de agosto? Nossa área de compras já comunicou a data internamente e vai reorganizar o fluxo atual na mesma semana.',
      'Se houver risco no prazo, prefiro saber agora para segurar a comunicação. Qualquer coisa, me chame por telefone.',
      'Abraço, Marcelo',
    ],
    receivedAt: '2026-08-07T09:18:00-03:00',
    unread: true,
    context: { code: 'WI-1183', kind: 'Item de Trabalho', route: '/work-items' },
  },
  {
    id: 'mail-3',
    senderName: 'Juliana Rezende',
    // dominio da propria empresa: nenhuma empresa correlacionado
    senderAddress: 'juliana.rezende@wynk.com.br',
    initials: 'JR',
    subject: 'Retrospectiva de julho — pontos para a reunião',
    snippet: 'Separei três pontos que apareceram em quase todas as conversas…',
    bodyParagraphs: [
      'Pessoal,',
      'Separei três pontos que apareceram em quase todas as conversas individuais de julho: fila de revisão de código parada no fim do dia, demandas chegando sem impacto descrito e falta de clareza sobre quem decide prioridade quando duas áreas pedem a mesma semana.',
      'Levo os três para a reunião de quinta. Se alguém quiser incluir mais alguma coisa, me avise até quarta ao meio-dia.',
    ],
    receivedAt: '2026-08-06T17:05:00-03:00',
    unread: false,
    context: null,
  },
  {
    id: 'mail-4',
    senderName: 'Fernando Beltrão',
    // dominio de "Rede Farmais"
    senderAddress: 'fernando@farmais.com.br',
    initials: 'FB',
    subject: 'Acesso de dois usuários novos',
    snippet: 'Entraram duas pessoas no time de operação e precisam de acesso…',
    bodyParagraphs: [
      'Oi, Ana!',
      'Entraram duas pessoas no time de operação e precisam de acesso à plataforma: Renata Coelho e Thiago Vasques, ambas no perfil de leitura das áreas de Logística e Atendimento.',
      'Podem começar na segunda-feira, dia 10. Se precisar de algum dado adicional das duas, é só pedir.',
    ],
    receivedAt: '2026-08-06T10:32:00-03:00',
    unread: false,
    context: null,
  },
  {
    id: 'mail-5',
    senderName: 'Sandra Okamoto',
    // segundo dominio do MESMA empresa — mostra que a correlacao e por dominio
    senderAddress: 'sandra.okamoto@bancomeridiano.com',
    initials: 'SO',
    subject: 'Confirmação da reunião de alinhamento',
    snippet: 'Confirmado para terça, dia 11, às 14h, no formato remoto.',
    bodyParagraphs: [
      'Ana, boa tarde.',
      'Confirmado para terça, dia 11, às 14h, no formato remoto. Vou levar o time de operação e a pessoa responsável pelo financeiro.',
      'Pauta combinada: fechamento de agosto, relatório de faturamento e próximos passos do módulo de aprovações.',
    ],
    receivedAt: '2026-08-05T14:47:00-03:00',
    unread: false,
    context: { code: 'Reunião de alinhamento · 11/08', kind: 'Reunião', route: '/meetings' },
  },
  {
    id: 'mail-6',
    senderName: 'Henrique Salles',
    senderAddress: 'henrique.salles@wynk.com.br',
    initials: 'HS',
    subject: 'Resultado da manutenção do banco',
    snippet: 'A janela de manutenção de sábado terminou às 5h12, sem incidentes.',
    bodyParagraphs: [
      'Bom dia,',
      'A janela de manutenção de sábado terminou às 5h12, sem incidentes. Os índices foram recriados e o tempo médio das consultas do quadro caiu de 780 ms para 210 ms.',
      'Deixei o relatório completo anexado ao documento de plantão. Próxima janela prevista para 12 de setembro.',
    ],
    receivedAt: '2026-08-03T07:26:00-03:00',
    unread: false,
    context: null,
  },
]
